import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as Companies from "@/models/companies";
import * as React from "react";

import { showErrorToast, showSuccessToast } from "turboui";
import { PageModule } from "@/routes/types";

import {
  ConversationDetail,
  ConversationMessage,
  InboxConversation,
  InboxPage as InboxPageData,
  acknowledgeHandoff,
  getConversation,
  getInbox,
  releaseConversation,
  replyToConversation,
  takeOverConversation,
} from "@/models/emailManagement";

export default { name: "CompanyInboxPage", loader, Page } as PageModule;

const PAGE_SIZE = 40;

type Ownership = "all" | "agent" | "human";

const OWNERSHIP_TABS: { value: Ownership; label: string }[] = [
  { value: "all", label: "All" },
  { value: "agent", label: "Agent-run" },
  { value: "human", label: "I took over" },
];

interface LoaderResult {
  company: Companies.Company;
  inbox: InboxPageData;
}

async function loader(): Promise<LoaderResult> {
  const company = await Companies.getCompany({ includePermissions: true }).then((r: any) => r.company);
  if (!company.permissions?.isAdmin) throw new Response("Not Found", { status: 404 });
  const inbox = await getInbox({ limit: PAGE_SIZE });
  return { company, inbox };
}

/* Mail-client time: clock for today, month/day this year, full date beyond. */
function formatWhen(iso?: string | null) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (sameDay) return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (date.getFullYear() === now.getFullYear())
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatFull(iso?: string | null) {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

function who(c: { counterpartName?: string | null; counterpartEmail: string }) {
  return c.counterpartName || c.counterpartEmail;
}

function Page() {
  const { inbox: initial } = Pages.useLoadedData() as LoaderResult;

  const [page, setPage] = React.useState<InboxPageData>(initial);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(true);

  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [ownership, setOwnership] = React.useState<Ownership>("all");
  const [awaitingOnly, setAwaitingOnly] = React.useState(false);

  // DEEP LINK from the Feed: /admin/inbox?conversation=<id>.
  //
  // The linked thread very often is NOT in the loaded list -- it may be past the first page, or
  // filtered out entirely by the current ownership/awaiting filters. That is fine for the reading
  // pane, which fetches a conversation by id on its own. It is NOT fine for the selection: the
  // "keep a valid selection" effect below resets to the top of the list whenever the selected id
  // is missing from it, which would yank the operator straight off the thread they clicked and
  // make the link look broken. So a deep-linked selection is PINNED, and stays pinned until the
  // operator picks a different conversation themselves.
  const deepLinkId = React.useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get("conversation");
    const trimmed = raw?.trim();
    return trimmed ? trimmed : undefined;
  }, []);

  const [pinnedId, setPinnedId] = React.useState<string | undefined>(deepLinkId);
  const [selectedId, setSelectedId] = React.useState<string | undefined>(
    deepLinkId ?? initial.conversations[0]?.conversationId,
  );
  const [detail, setDetail] = React.useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const [reason, setReason] = React.useState("");
  const [replyBody, setReplyBody] = React.useState("");
  const [busy, setBusy] = React.useState<"" | "takeover" | "release" | "reply" | "ack">("");

  // A slower earlier request must never overwrite a newer one's results.
  const ticket = React.useRef(0);

  // Search hits message bodies server-side, so debounce it.
  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const load = React.useCallback(
    async (offset: number, replace: boolean) => {
      const mine = ++ticket.current;
      setLoading(true);
      try {
        const next = await getInbox({
          search: search || undefined,
          ownership,
          awaitingOnly: awaitingOnly || undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (mine !== ticket.current) return;
        setPage((current) =>
          replace ? next : { ...next, conversations: [...current.conversations, ...next.conversations] },
        );
      } catch {
        if (mine === ticket.current) showErrorToast("Error", "Could not load the inbox");
      } finally {
        if (mine === ticket.current) {
          setLoading(false);
          setLoaded(true);
        }
      }
    },
    [search, ownership, awaitingOnly],
  );

  React.useEffect(() => {
    setLoaded(false);
    void load(0, true);
  }, [load]);

  const loadDetail = React.useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      setDetail(await getConversation(id));
    } catch {
      showErrorToast("Error", "Could not open that conversation");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  // Keep a valid selection as filters change, without stealing it mid-read.
  React.useEffect(() => {
    if (!loaded) return;
    // A pinned deep link owns the selection even while it is absent from the filtered list.
    if (pinnedId && selectedId === pinnedId) return;
    const list = page.conversations;
    const first = list[0];
    if (!first) {
      setSelectedId(undefined);
      return;
    }
    if (!selectedId || !list.some((c) => c.conversationId === selectedId)) {
      setSelectedId(first.conversationId);
    }
  }, [page.conversations, loaded, selectedId, pinnedId]);

  const refreshBoth = React.useCallback(() => {
    void load(0, true);
    if (selectedId) void loadDetail(selectedId);
  }, [load, loadDetail, selectedId]);

  const takenOver = !!detail?.takenOver;

  async function onTakeOver() {
    if (!selectedId) return;
    setBusy("takeover");
    try {
      const res: any = await takeOverConversation({ conversationId: selectedId, reason: reason.trim() || undefined });
      const killed = res?.cancelledPending ?? 0;
      showSuccessToast(
        "You have this one",
        killed > 0
          ? `Agents are locked out of this contact. ${killed} queued message${killed === 1 ? "" : "s"} cancelled.`
          : "Agents are locked out of this contact.",
      );
      setReason("");
      refreshBoth();
    } catch {
      showErrorToast("Error", "Could not take this conversation over");
    } finally {
      setBusy("");
    }
  }

  async function onRelease() {
    if (!selectedId) return;
    setBusy("release");
    try {
      await releaseConversation(selectedId);
      showSuccessToast("Handed back", "Agents can email this contact again.");
      refreshBoth();
    } catch {
      showErrorToast("Error", "Could not hand this conversation back");
    } finally {
      setBusy("");
    }
  }

  // Opening a thread clears its unread badge: the server stamps the shared seen marker when
  // the detail loads; this mirrors it locally so the dot disappears without a list refetch.
  function selectConversation(id: string) {
    // An explicit pick releases the deep-link pin: from here on the list governs the selection again.
    setPinnedId(undefined);
    setSelectedId(id);
    setPage((p) => ({
      ...p,
      conversations: p.conversations.map((c) => (c.conversationId === id ? { ...c, unread: false } : c)),
    }));
  }

  async function onAcknowledge() {
    if (!selectedId) return;
    setBusy("ack");
    try {
      await acknowledgeHandoff(selectedId);
      showSuccessToast("Acknowledged", "The text alerts stop. The lead still shows here until your reply goes out.");
      refreshBoth();
    } catch {
      showErrorToast("Error", "Could not acknowledge this alert");
    } finally {
      setBusy("");
    }
  }

  async function onReply() {
    if (!selectedId || !replyBody.trim()) return;
    setBusy("reply");
    try {
      const res: any = await replyToConversation({ conversationId: selectedId, body: replyBody });
      if (res?.status === "failed") {
        showErrorToast("Not sent", res.error || "The mailbox refused the message.");
      } else {
        showSuccessToast("Sent", "Your reply went out in this thread.");
        setReplyBody("");
      }
      refreshBoth();
    } catch {
      showErrorToast("Error", "The reply could not be sent");
    } finally {
      setBusy("");
    }
  }

  const awaiting = page.conversations.filter((c) => c.awaitingReply).length;
  const held = page.conversations.filter((c) => c.takenOver).length;

  return (
    <Pages.Page title={["Inbox"]}>
      <Paper.Root size="xlarge">
        <Paper.Body>
          <h1 className="text-2xl font-bold">Inbox</h1>
          <p className="text-sm text-content-dimmed mt-1 mb-6">
            Every conversation this company has sent or received, newest first. Take one over and no agent can email
            that person again until you hand it back.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="flex border border-surface-outline rounded overflow-hidden">
              {OWNERSHIP_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setOwnership(tab.value)}
                  className={`px-3 py-1.5 text-sm ${
                    ownership === tab.value ? "bg-surface-accent text-content-accent font-semibold" : "text-content-dimmed"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <label className="flex items-center gap-2 text-sm text-content-dimmed">
              <input type="checkbox" checked={awaitingOnly} onChange={(e) => setAwaitingOnly(e.target.checked)} />
              Awaiting our reply
            </label>

            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search people, subjects, message text…"
              className="flex-1 min-w-[240px] border border-surface-outline rounded px-3 py-1.5 text-sm bg-surface-base"
            />

            <div className="text-xs text-content-dimmed whitespace-nowrap">
              {page.total} total · {awaiting} awaiting · {held} held
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-5 items-start">
            <ConversationList
              page={page}
              loaded={loaded}
              loading={loading}
              selectedId={selectedId}
              onSelect={selectConversation}
              onLoadMore={() => void load(page.offset + PAGE_SIZE, false)}
              filtered={!!search || ownership !== "all" || awaitingOnly}
            />

            <div className="flex flex-col gap-5">
              {detailLoading && !detail ? (
                <Panel>
                  <div className="p-5 text-sm text-content-dimmed">Opening…</div>
                </Panel>
              ) : detail ? (
                <>
                  {detail.handoffAlert ? (
                    <HandoffBanner alert={detail.handoffAlert} busy={busy} onAcknowledge={onAcknowledge} />
                  ) : null}
                  <ConversationHeader
                    detail={detail}
                    takenOver={takenOver}
                    busy={busy}
                    reason={reason}
                    setReason={setReason}
                    onTakeOver={onTakeOver}
                    onRelease={onRelease}
                  />
                  <Composer
                    detail={detail}
                    takenOver={takenOver}
                    busy={busy}
                    replyBody={replyBody}
                    setReplyBody={setReplyBody}
                    onReply={onReply}
                  />
                  <Thread detail={detail} />
                </>
              ) : (
                <Panel>
                  <div className="p-5 text-sm text-content-dimmed">
                    Pick a conversation to read the full exchange and take it over.
                  </div>
                </Panel>
              )}
            </div>
          </div>
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

/** A hot lead the agents handed to you. Nagging = your phone keeps buzzing until you act here. */
function HandoffBanner({
  alert,
  busy,
  onAcknowledge,
}: {
  alert: NonNullable<ConversationDetail["handoffAlert"]>;
  busy: string;
  onAcknowledge: () => void;
}) {
  const nagging = alert.status === "nagging";
  return (
    <div
      className={`border rounded px-4 py-3 flex items-center justify-between gap-4 ${
        nagging ? "border-red-500 bg-red-50 dark:bg-red-950/30" : "border-surface-outline bg-surface-dimmed"
      }`}
    >
      <div className="text-sm">
        <span className="font-semibold">{nagging ? "🔥 Hot lead — handed to you." : "Hot lead — acknowledged, not yet answered."}</span>{" "}
        {alert.headline ? <span>{alert.headline}. </span> : null}
        {nagging
          ? "Text alerts repeat until you press Acknowledge or your reply goes out."
          : "The texts have stopped; this stays here until your reply goes out."}
      </div>
      {nagging ? (
        <button
          onClick={onAcknowledge}
          disabled={busy === "ack"}
          className="shrink-0 text-sm font-semibold px-3 py-1.5 rounded border border-red-500 text-red-700 dark:text-red-300"
        >
          {busy === "ack" ? "Stopping…" : "Acknowledge"}
        </button>
      ) : null}
    </div>
  );
}

function Panel({ children, title }: { children: React.ReactNode; title?: React.ReactNode }) {
  return (
    <div className="border border-surface-outline rounded bg-surface-base">
      {title ? <div className="border-b border-surface-outline px-4 py-2.5 font-semibold text-sm">{title}</div> : null}
      {children}
    </div>
  );
}

function AuthorTag({ kind }: { kind: ConversationMessage["authorKind"] }) {
  const map = {
    human: { label: "You", cls: "bg-surface-accent text-content-accent" },
    counterpart: { label: "Them", cls: "bg-surface-dimmed text-content-dimmed" },
    agent: { label: "Agent", cls: "bg-surface-dimmed text-content-dimmed" },
  } as const;
  const t = map[kind] ?? map.agent;
  return <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${t.cls}`}>{t.label}</span>;
}

function ConversationList({
  page,
  loaded,
  loading,
  selectedId,
  onSelect,
  onLoadMore,
  filtered,
}: {
  page: InboxPageData;
  loaded: boolean;
  loading: boolean;
  selectedId?: string;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
  filtered: boolean;
}) {
  return (
    <Panel title="Conversations">
      <div className="max-h-[70vh] overflow-y-auto">
        {!loaded ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-surface-dimmed animate-pulse rounded" />
            ))}
          </div>
        ) : page.conversations.length ? (
          <>
            {page.conversations.map((c, i) => (
              <ConversationRow
                key={c.conversationId}
                conversation={c}
                active={c.conversationId === selectedId}
                first={i === 0}
                onSelect={onSelect}
              />
            ))}
            {page.conversations.length < page.total ? (
              <div className="p-3 border-t border-surface-outline">
                <button
                  onClick={onLoadMore}
                  disabled={loading}
                  className="w-full text-sm py-1.5 border border-surface-outline rounded"
                >
                  {loading ? "Loading…" : `Load ${Math.min(PAGE_SIZE, page.total - page.conversations.length)} more`}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className="p-5 text-sm text-content-dimmed">
            {filtered
              ? "Nothing matches these filters."
              : "No mail yet. Conversations appear the moment an agent reaches out or someone replies."}
          </div>
        )}
      </div>
    </Panel>
  );
}

function ConversationRow({
  conversation: c,
  active,
  first,
  onSelect,
}: {
  conversation: InboxConversation;
  active: boolean;
  first: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onSelect(c.conversationId)}
      className={`block w-full text-left px-4 py-3 ${first ? "" : "border-t border-surface-outline"} ${
        active ? "bg-surface-highlight" : "hover:bg-surface-dimmed"
      }`}
    >
      <div className="flex items-baseline gap-2">
        {c.unread ? (
          <span
            className="inline-block w-2 h-2 rounded-full bg-accent-1 shrink-0 self-center"
            aria-label="Unread"
            title="New activity since you last opened this"
          />
        ) : null}
        <span className={`flex-1 truncate text-sm ${c.unread || c.awaitingReply ? "font-bold" : "font-medium"}`}>
          {who(c)}
        </span>
        <span className="text-xs text-content-dimmed whitespace-nowrap" title={formatFull(c.lastAt)}>
          {formatWhen(c.lastAt)}
        </span>
      </div>

      <div className="truncate text-sm mt-0.5">
        {c.lastDirection === "inbound" ? "↙ " : "↗ "}
        {c.subject || "(no subject)"}
      </div>

      <div className="truncate text-xs text-content-dimmed mt-0.5">{c.preview || "No message body."}</div>

      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        {c.takenOver ? (
          <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-surface-accent text-content-accent">
            {c.takenOverBy ? `Held by ${c.takenOverBy}` : "Human control"}
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-surface-dimmed text-content-dimmed">
            Agent-run
          </span>
        )}
        {c.identityName ? <span className="text-[11px] text-content-dimmed">via {c.identityName}</span> : null}
        {c.messageCount > 1 ? <span className="text-[11px] text-content-dimmed">{c.messageCount} messages</span> : null}
      </div>
    </button>
  );
}

function ConversationHeader({
  detail,
  takenOver,
  busy,
  reason,
  setReason,
  onTakeOver,
  onRelease,
}: {
  detail: ConversationDetail;
  takenOver: boolean;
  busy: string;
  reason: string;
  setReason: (v: string) => void;
  onTakeOver: () => void;
  onRelease: () => void;
}) {
  return (
    <Panel title={detail.subject || "(no subject)"}>
      <div className="p-4 flex flex-wrap gap-5 justify-between">
        <div className="min-w-0">
          <div className="text-sm font-semibold">
            {who(detail)} <span className="font-normal text-content-dimmed">{detail.counterpartEmail}</span>
          </div>
          <div className="text-xs text-content-dimmed mt-1">
            Sending as {detail.identityName || "—"}
            {detail.identityRole ? ` · ${detail.identityRole}` : ""}
            {detail.identityAddress ? ` · ${detail.identityAddress}` : ""}
          </div>
          {takenOver ? (
            <p className="text-xs text-content-dimmed mt-2 max-w-xl">
              {detail.takenOverBy ? `${detail.takenOverBy} took this contact over` : "Taken over"} on{" "}
              {formatFull(detail.takenOverAt)}. No agent can email {detail.counterpartEmail} until it is handed back.
              {detail.takeoverReason ? ` Reason: ${detail.takeoverReason}` : ""}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col items-end gap-2">
          {takenOver ? (
            <button
              onClick={onRelease}
              disabled={busy === "release"}
              className="px-3 py-1.5 text-sm border border-surface-outline rounded"
            >
              {busy === "release" ? "Handing back…" : "Hand back to agents"}
            </button>
          ) : (
            <>
              <button
                onClick={onTakeOver}
                disabled={busy === "takeover"}
                className="px-3 py-1.5 text-sm rounded bg-accent-1 text-white-1 font-semibold"
              >
                {busy === "takeover" ? "Taking over…" : "Take over"}
              </button>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why? (optional)"
                className="w-56 border border-surface-outline rounded px-2 py-1 text-xs bg-surface-base"
              />
            </>
          )}
        </div>
      </div>
    </Panel>
  );
}

const THREAD_PREVIEW_COUNT = 2;

/** Newest message on top, latest two expanded; the older tail stays behind one expander so a
 *  long thread never needs scrolling to see what just happened or to reply. */
function Thread({ detail }: { detail: ConversationDetail }) {
  const [showAll, setShowAll] = React.useState(false);
  React.useEffect(() => setShowAll(false), [detail.conversationId]);

  const newestFirst = [...detail.messages].reverse();
  const visible = showAll ? newestFirst : newestFirst.slice(0, THREAD_PREVIEW_COUNT);
  const hidden = newestFirst.length - visible.length;

  return (
    <Panel title="Thread · newest first">
      {visible.map((m, i) => (
        <article
          key={m.id}
          className={`px-4 py-3 ${i ? "border-t border-surface-outline" : ""} ${
            m.direction === "inbound" ? "bg-surface-dimmed" : ""
          }`}
        >
          <header className="flex flex-wrap items-center gap-2 text-xs text-content-dimmed">
            <AuthorTag kind={m.authorKind} />
            <span className="font-medium text-content-base">{m.from}</span>
            <span>to {m.to}</span>
            {m.authorName ? <span>· {m.authorName}</span> : null}
            <span className="ml-auto">{formatFull(m.at)}</span>
          </header>

          {m.subject && m.subject !== detail.subject ? (
            <div className="text-sm font-semibold mt-2">{m.subject}</div>
          ) : null}

          <p className="text-sm whitespace-pre-wrap mt-2">{m.body}</p>

          <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-content-dimmed">
            <span>{m.status}</span>
            {m.error ? <span className="text-content-error">{m.error}</span> : null}
          </div>
        </article>
      ))}
      {hidden > 0 ? (
        <div className="p-3 border-t border-surface-outline">
          <button
            onClick={() => setShowAll(true)}
            className="w-full text-sm py-1.5 border border-surface-outline rounded text-content-dimmed hover:bg-surface-dimmed"
          >
            Show {hidden} earlier message{hidden === 1 ? "" : "s"}
          </button>
        </div>
      ) : null}
      {showAll && newestFirst.length > THREAD_PREVIEW_COUNT ? (
        <div className="p-3 border-t border-surface-outline">
          <button
            onClick={() => setShowAll(false)}
            className="w-full text-sm py-1.5 border border-surface-outline rounded text-content-dimmed hover:bg-surface-dimmed"
          >
            Collapse earlier messages
          </button>
        </div>
      ) : null}
    </Panel>
  );
}

function Composer({
  detail,
  takenOver,
  busy,
  replyBody,
  setReplyBody,
  onReply,
}: {
  detail: ConversationDetail;
  takenOver: boolean;
  busy: string;
  replyBody: string;
  setReplyBody: (v: string) => void;
  onReply: () => void;
}) {
  if (!takenOver) {
    return (
      <Panel title="Reply">
        <div className="p-5 text-sm text-content-dimmed">
          Take this conversation over before replying. Writing while an agent still owns the contact would put two
          voices in the same thread.
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Reply">
      <div className="p-4">
        <textarea
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          placeholder={`Write to ${who(detail)}…`}
          rows={6}
          className="w-full border border-surface-outline rounded px-3 py-2 text-sm bg-surface-base"
        />
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <button
            onClick={onReply}
            disabled={busy === "reply" || !replyBody.trim()}
            className="px-3 py-1.5 text-sm rounded bg-accent-1 text-white-1 font-semibold disabled:opacity-50"
          >
            {busy === "reply" ? "Sending…" : "Send reply"}
          </button>
          <span className="text-xs text-content-dimmed">
            Goes out as {detail.identityName || "the pinned sender"} ({detail.identityAddress}) with their signature, so
            the thread keeps one voice. Sends immediately, not on the drip.
          </span>
        </div>
      </div>
    </Panel>
  );
}
